import { fetcher } from '@/src/general/utilities/fetcher'
import type { TSignalConfig } from '@/src/general/utilities/fetcher/IFetcher'

import type { NOTIFICATION_TYPE } from '../models/notification.constants'
import type { TGetNotificationsPayload } from './types/getNotifications.types'
import type { TGetNotificationSettingsData, TGetNotificationSettingsPayload } from './types/getNotificationSettings'
import type { TGetPushSettingsDevicesData } from './types/getPushSettingsDevices.types'
import type { TNotificationApi } from './types/notificationApi.types'
import type {
  TPatchNotificationSettingsData,
  TPatchNotificationSettingsPayload,
} from './types/patchNotificationSettings'
import type { TPostDeviceData, TPostDevicePayload } from './types/postDevice.types'

export const notificationApi = {
  getNotifications: <Type extends NOTIFICATION_TYPE>(
    payload: TGetNotificationsPayload<Type>,
    { signal }: TSignalConfig,
  ) =>
    fetcher.query<TNotificationApi<Type>[]>('/notifications', {
      params: payload,
      signal,
    }),
  getPushSettingsDevices: ({ signal }: TSignalConfig) =>
    fetcher.query<TGetPushSettingsDevicesData>('/me/settings/devices', { signal }),
  markAsRead: (id: number | NOTIFICATION_TYPE) =>
    fetcher.mutate<void>(`/notification/${id}/mark-as-read`, {}, { method: 'POST' }),
  postDevice: (payload: TPostDevicePayload) =>
    fetcher.mutate<TPostDeviceData, TPostDevicePayload>('/push/device', payload, { method: 'POST' }),
  getNotificationSettings: (params: TGetNotificationSettingsPayload, { signal }: TSignalConfig) => {
    const { updateParametersToken, ...otherParams } = params
    const cleanParams = {
      ...otherParams,
      ...(updateParametersToken && { updateParametersToken }),
    }
    return fetcher.query<TGetNotificationSettingsData>('/me/settings/notifications', { params: cleanParams, signal })
  },
  patchNotificationSettings: ({ updateParametersToken, ...values }: TPatchNotificationSettingsPayload) =>
    fetcher.mutate<TPatchNotificationSettingsData, Omit<TPatchNotificationSettingsPayload, 'updateParametersToken'>>(
      '/me/settings/notifications',
      values,
      { method: 'PATCH', params: { updateParametersToken } },
    ),
}
